import { flowRight, isNil, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import { FORUM_APP_ID } from '@wix/communities-universal/src/constants/appsConfig';
import {
  ROLE_OWNER,
  ROLE_ADMIN,
  ROLE_MODERATOR,
  ROLE_WIX_EXPERT,
  ROLE_WIX_CODE_EXPERT,
  ROLE_WIX_ARENA_MEMBER,
  ROLE_WIX_CODE_FORUM_NINJA,
  ROLE_WIX_TRIBE_LEADER,
  ROLE_WIX_COMMUNITY_CHAMP,
  ROLE_WIX_CON_MIAMI,
} from '../../../common/components/permissions-context/permissions-checker';
import TimeAgo from '../time-ago';
import { getUserRole } from '../../services/user-role';
import { getUserBadges } from '../../services/user-badge';
import { BulletSeparator } from '../separator';
import withTranslate from '../../hoc/with-translate';
import styles from './quick-user-info.scss';

const roleToTranslation = {
  [ROLE_OWNER]: 'role.admin',
  [ROLE_ADMIN]: 'role.admin',
  [ROLE_MODERATOR]: 'role.moderator',

  [ROLE_WIX_EXPERT]: 'role.wixExpert',
  [ROLE_WIX_CODE_EXPERT]: 'role.wixCodeExpert',
  [ROLE_WIX_ARENA_MEMBER]: 'role.wixArenaMember',
  [ROLE_WIX_CODE_FORUM_NINJA]: 'role.wixCodeForumNinja',
  [ROLE_WIX_TRIBE_LEADER]: 'role.wixTribeLeader',
  [ROLE_WIX_COMMUNITY_CHAMP]: 'role.wixCommunityChamp',
  [ROLE_WIX_CON_MIAMI]: 'role.wixConMiami',
};

function mapRoleToTranslation(role, t) {
  const tKey = roleToTranslation[role];
  return tKey ? t(tKey) : null;
}

function getCounters(counters = {}) {
  const forumCounters = counters[FORUM_APP_ID];
  if (!forumCounters) {
    return null;
  }
  return {
    comments: forumCounters.commentsCount,
    likes: (forumCounters.likedCommentsCount || 0) + (forumCounters.likedPostsCount || 0),
    topComments: forumCounters.topCommentsCount,
  };
}

function CountersInfo({ likes, comments, topComments, t }) {
  const isLikesValid = !isNil(likes);
  const isCommentsValid = !isNil(comments);
  const isTopCommentsValid = !isNil(topComments);
  return (
    <React.Fragment>
      {isLikesValid && `${likes} ${t('quick-user-info.likes')}`}
      {false && isCommentsValid && <BulletSeparator />}
      {false && isCommentsValid && `${comments} ${t('quick-user-info.comments')}`}
      {isTopCommentsValid && <BulletSeparator />}
      {isTopCommentsValid && `${topComments} ${t('quick-user-info.top-comments')}`}
    </React.Fragment>
  );
}

CountersInfo.propTypes = {
  likes: PropTypes.number,
  comments: PropTypes.number,
  topComments: PropTypes.number,
  t: PropTypes.func,
};

function QuickUserInfo({ user = {}, date, showDate, showBadges, t }) {
  const counters = getCounters(user.counters);
  let content;

  if (showDate) {
    content = <TimeAgo time={date} className={styles.date} />;
  } else if (counters) {
    content = (
      <CountersInfo
        likes={counters.likes}
        comments={counters.comments}
        topComments={counters.topComments}
        t={t}
      />
    );
  } else {
    content = null;
  }

  const role = getUserRole(user);
  const badges = showBadges ? getUserBadges(user) : [];
  const roles = [role, ...badges]
    .map(r => mapRoleToTranslation(r, t))
    .filter(role => role !== null);

  const renderRole = (role, idx) => (
    <span key={role}>
      {idx > 0 ? <BulletSeparator /> : null}
      {role}
    </span>
  );

  return (
    <span className={classNames(styles.container)} data-hook="quick-user-info">
      {!isEmpty(roles) && roles.map(renderRole)}
      {content && !isEmpty(roles) && <BulletSeparator />}
      {content}
    </span>
  );
}

QuickUserInfo.propTypes = {
  user: PropTypes.object,
  type: PropTypes.string,
  date: PropTypes.string,
  t: PropTypes.func,
  showDate: PropTypes.bool,
  showBadges: PropTypes.bool,
};

QuickUserInfo.defaultProps = {
  showBadges: true,
};

export default flowRight(withTranslate)(QuickUserInfo);
